import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import { fetchScorecardCohort } from '../../services/contactOptimizationService';
import type { ScorecardCohortResponse, ScorecardCohortRow } from '../../types/contactOptimization';
import SectionHeader, { HeaderIconButton } from './SectionHeader';
import ExplainerNote from './ExplainerNote';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CohortRangePicker, { defaultCohortRange, type CohortRange } from './CohortRangePicker';
import CohortLeadsDialog from './CohortLeadsDialog';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { useRefreshRegistration } from './RefreshController';

const GRAIN_NOUN: Record<string, string> = { day: 'day', week: 'week', month: 'month' };
// Adverbs, not noun + "ly": "day" + "ly" gives "dayly".
const GRAIN_ADVERB: Record<string, string> = { day: 'Daily', week: 'Weekly', month: 'Monthly' };

function pct(n: number, of: number): number | null {
  return of ? (n / of) * 100 : null;
}

function fmtPct(v: number | null): string {
  if (v === null) return '—';
  return v >= 9.95 ? `${Math.round(v)}%` : `${v.toFixed(1)}%`;
}

/** "12% (26)" on one line. Used by the CSV, where a single flat string per
 * field is the only sensible shape. The on-screen table stacks the two
 * instead -- see StackedCell. */
function fmtCell(count: number | null | undefined, of: number): string {
  if (count === null || count === undefined) return '—';
  return `${fmtPct(pct(count, of))} (${count.toLocaleString()})`;
}

/** The share on top, the count smaller underneath.
 *
 * Stacked rather than inline because "12% (1,234)" side by side forced every
 * numeric column wide enough for the widest count, pushing the table past
 * 1280px and off-screen. Stacking sizes each column to the percentage
 * instead, which is the number being scanned; the count is there to make it
 * trustworthy, not to be read first. */
function StackedCell({ count, of }: { count: number | null | undefined; of: number }) {
  if (count === null || count === undefined) return <>&mdash;</>;
  return (
    <>
      <Box sx={{ lineHeight: 1.2 }}>{fmtPct(pct(count, of))}</Box>
      <Box sx={{ fontSize: '0.66rem', lineHeight: 1.2, opacity: 0.6 }}>
        ({count.toLocaleString()})
      </Box>
    </>
  );
}

function exportCsv(data: ScorecardCohortResponse) {
  const offsets = Array.from({ length: data.max_offset + 1 }, (_, i) => i);
  // Deliberately a faithful copy of what is on screen, "12% (26)" and all,
  // so a pasted export and a screenshot cannot disagree. Raw counts are
  // still recoverable from the brackets if anyone needs to compute on them.
  //
  // Every field goes through the shared escaper, header included: one of
  // these labels contains a comma, and quoting only the data cells is what
  // shifted every column right of it by one in an earlier version.
  const header = [
    'Arrived', 'From', 'To', 'Leads',
    ...offsets.map((o) => `+${o}d`),
    'Later', 'Dialed, no connect', 'Never dialed', 'Total contacted',
    'Still filling in', 'Clipped by range',
  ];
  const rows = data.rows.map((r) => [
    r.label, r.bucket_start, r.bucket_end, r.leads,
    ...offsets.map((o) => fmtCell(r.offsets[String(o)], r.leads)),
    fmtCell(r.later, r.leads),
    fmtCell(r.dialed_never, r.leads),
    fmtCell(r.never_dialed, r.leads),
    fmtCell(r.total_contacted, r.leads),
    r.mature ? '' : 'yes',
    r.partial ? 'yes' : '',
  ]);
  downloadCsv(
    `cohort-by-arrival-${data.period.start}-to-${data.period.end}.csv`,
    buildCsv(header, rows),
  );
}

/**
 * Leads grouped by the day they arrived, against how many days later we
 * first got them on the phone.
 *
 * Rows are newest first, and roll up to weeks or months as the range
 * widens so the row count stays readable. Columns never change.
 *
 * IMPORTANT: this is not the same measurement as the Pulse tab's contact
 * speed table, and the two will disagree on the same leads. Pulse buckets
 * a lead by its FIRST CALL and counts it contacted only if that call
 * connected. This buckets a lead by its FIRST CONNECT, however many
 * attempts that took, so it reports higher rates. The explainer below says
 * so in as many words, because otherwise someone reasonably files one of
 * them as a bug.
 *
 * Carries its own date range, independent of the Scorecard's period
 * picker: changing one must not refetch the other.
 */
export default function ScorecardCohort() {
  const theme = useTheme();
  const [range, setRange] = React.useState<CohortRange>(defaultCohortRange);
  const [data, setData] = React.useState<ScorecardCohortResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showHelp, setShowHelp] = React.useState(false);
  const [openRow, setOpenRow] = React.useState<ScorecardCohortRow | null>(null);
  const [refreshing, setRefreshing] = React.useState(false);

  // Guards against a slow response for an old range landing after a newer
  // one: only the latest request is allowed to write state.
  const requestSeq = React.useRef(0);

  const load = React.useCallback(
    async (opts?: { refresh?: boolean }) => {
      const seq = ++requestSeq.current;
      if (opts?.refresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const res = await fetchScorecardCohort(
          { startDate: range.start, endDate: range.end },
          { refresh: opts?.refresh },
        );
        if (seq === requestSeq.current) setData(res);
      } catch (e) {
        if (seq === requestSeq.current) {
          setError(e instanceof Error ? e.message : 'Failed to load the cohort table');
          setData(null);
        }
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [range.start, range.end],
  );

  React.useEffect(() => {
    void load();
  }, [load]);

  // The page-level Refresh should rebuild this section too, not just the
  // pillars above it.
  useRefreshRegistration(() => load({ refresh: true }));

  const offsets = React.useMemo(
    () => Array.from({ length: (data?.max_offset ?? 6) + 1 }, (_, i) => i),
    [data?.max_offset],
  );

  // Shaded against the strongest single-day share in view, so the decay
  // shape reads at a glance. Scaled to the data rather than to 100%,
  // because at a ~13% contact rate a 0-100 scale renders every cell blank.
  const peak = React.useMemo(() => {
    let max = 0;
    for (const r of data?.rows ?? []) {
      for (const o of offsets) {
        const v = r.offsets[String(o)];
        if (v !== null && v !== undefined) max = Math.max(max, pct(v, r.leads) ?? 0);
      }
    }
    return max || 1;
  }, [data, offsets]);

  const cellBg = (value: number | null | undefined, leads: number) => {
    if (value === null || value === undefined) return 'transparent';
    const p = pct(value, leads) ?? 0;
    if (p <= 0) return 'transparent';
    return alpha(theme.palette.info.main, 0.05 + Math.min(1, p / peak) * 0.55);
  };

  const numCell = { px: 0.75, py: 0.6, textAlign: 'center' as const, fontVariantNumeric: 'tabular-nums' };

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        p: 2,
        mt: 2.5,
        border: `1px solid ${t.tokens.border.subtle}`,
        borderRadius: `${t.ds.radius.md}px`,
        bgcolor: t.tokens.surface.raised,
      })}
    >
      <SectionHeader
        eyebrow={data ? `${GRAIN_ADVERB[data.grain] ?? data.grain} rows · newest first` : undefined}
        title="Contact speed by arrival day"
        subtitle="Each row is a day leads came in. The columns show how many days later we first got them on the phone, so every lead in a row is counted exactly once and the row adds up to all of it."
        controls={<CohortRangePicker value={range} onChange={setRange} disabled={loading} />}
        utilities={
          <>
            <HeaderIconButton
              title={
                data?.from_cache
                  ? 'Rebuild this table from the warehouse now, ignoring the cached copy'
                  : 'Re-run this table against the warehouse'
              }
              ariaLabel="Refresh the cohort table"
              onClick={() => void load({ refresh: true })}
              disabled={loading || refreshing}
            >
              <RefreshRounded
                sx={{
                  fontSize: 16,
                  '@keyframes cohortSpin': { to: { transform: 'rotate(360deg)' } },
                  animation: refreshing ? 'cohortSpin 0.9s linear infinite' : undefined,
                }}
              />
            </HeaderIconButton>
            <HeaderIconButton
              title="Download this table as a CSV file"
              ariaLabel="Download the cohort table as CSV"
              onClick={() => data && exportCsv(data)}
              disabled={!data?.rows.length}
            >
              <DownloadRounded sx={{ fontSize: 16 }} />
            </HeaderIconButton>
            <HeaderIconButton
              title="How to read this table"
              ariaLabel="How to read this table"
              active={showHelp}
              onClick={() => setShowHelp((v) => !v)}
            >
              <InfoOutlined sx={{ fontSize: 16 }} />
            </HeaderIconButton>
          </>
        }
      />
      <ExplainerNote title="How to read this table" open={showHelp}>
        {`Each row is a group of leads by **when they arrived**. The **+Nd** columns show what share of that group we **first got on the phone** that many days later. A lead is counted in exactly one column, so the eleven outcome columns always add up to the whole row:

- **+0d to +6d** — first connected that many days after arriving.
- **Later** — first connected after day 6, within a ${data?.reachability_days ?? 15}-day window from arrival.
- **Dialed, no connect** — we called at least once and never got through.
- **Never dialed** — no call at all. Not "hard to reach", **never tried**.

**This is not the same number as the Pulse tab's contact speed table, and the two will disagree.** Pulse asks "did the *first call* connect?" and buckets a lead by the day it was first dialed. This asks "when did we *first get through*?" and buckets by the day of the first connect, however many attempts that took. A lead dialed Monday with no answer that connects Wednesday counts as +0/not-contacted on Pulse and **+2/contacted here**. So this table reports higher rates on the same leads. That is the intended difference, not drift.

Rows are newest first, and become weeks or months as the range widens so the table stays readable. Column meaning never changes with grain: a weekly row's +1d still means "one day after that particular lead arrived", not one day after the week began.

A **—** means not yet knowable rather than zero: a lead that arrived two days ago cannot have a +6d outcome yet. Those rows are marked as still filling in. Only counts leads that qualify as MQLs, matching the Queue and Pulse tabs. "Connect" is the same 90+ second definition used everywhere else.`}
      </ExplainerNote>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={22} />
        </Stack>
      ) : !data?.rows.length && !error ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No qualifying leads arrived in this range.
        </Typography>
      ) : data ? (
        <>
          <Box sx={{ mt: 1.5, overflowX: 'auto' }}>
            <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 940 }}>
              <Box component="thead">
                <Box component="tr">
                  {[
                    { k: 'arrived', l: 'Arrived', align: 'left' },
                    { k: 'leads', l: 'Leads', align: 'center' },
                    ...offsets.map((o) => ({ k: `d${o}`, l: `+${o}d`, align: 'center' })),
                    { k: 'later', l: 'Later', align: 'center' },
                    { k: 'dn', l: 'Dialed, no connect', align: 'center' },
                    { k: 'nd', l: 'Never dialed', align: 'center' },
                    { k: 'tot', l: 'Total contacted', align: 'center' },
                    { k: 'act', l: '', align: 'center' },
                  ].map((h) => (
                    <Box
                      key={h.k}
                      component="th"
                      sx={(t) => ({
                        ...numCell,
                        textAlign: h.align as 'left' | 'center',
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        color: t.palette.text.disabled,
                        borderBottom: `1px solid ${t.tokens.border.subtle}`,
                        // Wrapping, not nowrap: "Dialed, no connect" on one
                        // line would set that column's width for the whole
                        // table. Two short lines cost nothing in a header.
                        whiteSpace: 'normal',
                        lineHeight: 1.25,
                        verticalAlign: 'bottom',
                      })}
                    >
                      {h.l}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {data.rows.map((row) => (
                  <Box
                    component="tr"
                    key={`${row.bucket_start}-${row.bucket_end}`}
                    onClick={() => setOpenRow(row)}
                    sx={(t) => ({
                      cursor: 'pointer',
                      // Hover must not set bgcolor on every cell: the day
                      // columns paint their own heat shading, and a row-level
                      // background would win on specificity and flatten the
                      // very thing the shading exists to show. So the row
                      // gets an underline instead, which leaves colour alone.
                      '&:hover td': {
                        boxShadow: `inset 0 -2px 0 ${alpha(t.palette.primary.main, 0.45)}`,
                      },
                    })}
                  >
                    <Box
                      component="td"
                      sx={(t) => ({
                        px: 1,
                        py: 0.75,
                        whiteSpace: 'nowrap',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        borderBottom: `1px solid ${t.tokens.border.subtle}`,
                      })}
                    >
                      {row.label}
                      {!row.mature && (
                        <Tooltip title="Still filling in — these leads have not had a full week yet, so the later columns are incomplete" arrow>
                          <Box component="span" sx={{ ml: 0.5, color: 'warning.main', cursor: 'help' }}>○</Box>
                        </Tooltip>
                      )}
                      {row.partial && (
                        <Tooltip title="This bucket is clipped by the selected date range, so it covers fewer days than a full period" arrow>
                          <Box component="span" sx={{ ml: 0.5, color: 'text.disabled', cursor: 'help' }}>◐</Box>
                        </Tooltip>
                      )}
                    </Box>
                    <Box component="td" sx={(t) => ({ ...numCell, fontSize: '0.78rem', color: t.palette.text.secondary, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
                      {row.leads.toLocaleString()}
                    </Box>
                    {offsets.map((o) => {
                      const v = row.offsets[String(o)];
                      return (
                        <Box
                          key={o}
                          component="td"
                          sx={(t) => ({
                            ...numCell,
                            fontSize: '0.78rem',
                            bgcolor: cellBg(v, row.leads),
                            borderBottom: `1px solid ${t.tokens.border.subtle}`,
                            color: v === null || v === undefined ? t.palette.text.disabled : undefined,
                          })}
                        >
                          <Tooltip
                            title={
                              v === null || v === undefined
                                ? 'Not yet knowable: these leads have not had this many days yet'
                                : `${v.toLocaleString()} of ${row.leads.toLocaleString()} leads first connected on day +${o}`
                            }
                            arrow
                          >
                            <Box component="span" sx={{ display: 'inline-block' }}>
                              <StackedCell count={v} of={row.leads} />
                            </Box>
                          </Tooltip>
                        </Box>
                      );
                    })}
                    <Box component="td" sx={(t) => ({ ...numCell, fontSize: '0.78rem', color: t.palette.text.secondary, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
                      <StackedCell count={row.later} of={row.leads} />
                    </Box>
                    <Box component="td" sx={(t) => ({ ...numCell, fontSize: '0.78rem', color: t.palette.text.secondary, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
                      <StackedCell count={row.dialed_never} of={row.leads} />
                    </Box>
                    <Box component="td" sx={(t) => ({ ...numCell, fontSize: '0.78rem', color: t.palette.warning.main, fontWeight: 600, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
                      <Tooltip title={`${row.never_dialed.toLocaleString()} leads never received a single call`} arrow>
                        <Box component="span" sx={{ display: 'inline-block' }}>
                          <StackedCell count={row.never_dialed} of={row.leads} />
                        </Box>
                      </Tooltip>
                    </Box>
                    <Box component="td" sx={(t) => ({ ...numCell, fontSize: '0.82rem', fontWeight: 700, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
                      <StackedCell count={row.total_contacted} of={row.leads} />
                    </Box>
                    <Box
                      component="td"
                      sx={(t) => ({
                        ...numCell,
                        borderBottom: `1px solid ${t.tokens.border.subtle}`,
                      })}
                    >
                      <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenRow(row);
                        }}
                        sx={{ textTransform: 'none', fontSize: '0.72rem', whiteSpace: 'nowrap' }}
                      >
                        View leads
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
            {data.rows.length} {GRAIN_NOUN[data.grain] ?? data.grain}
            {data.rows.length === 1 ? '' : 's'} · every row adds up to 100% · connect means 90+ seconds · data as of {data.data_as_of}
            {data.from_cache && data.computed_at
              ? ` · cached, built ${new Date(data.computed_at).toLocaleString()}`
              : ''}
          </Typography>
        </>
      ) : null}

      <CohortLeadsDialog row={openRow} open={Boolean(openRow)} onClose={() => setOpenRow(null)} />
    </Paper>
  );
}
